// src/LocationLabels.jsx
import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import "./LocationLabel.css";

// Hardcoded tooltips (static)
const tooltips = [
  { name: "🏤 Club House", coords: "c1", className: "project-5-label" },
  { name: "🅿️ Parking", coords: "c2", className: "project-5-label" },
  { name: "🏟 Ampitheater", coords: "c3", className: "project-5-label" },
  { name: "🚗 Car Parking", coords: "c4", className: "project-5-label" },
  { name: "🏄‍♀️ Kids Playzone", coords: "c5", className: "project-5-label" },
  { name: "🔥 Camp Fire", coords: "c6", className: "project-5-label" },
];

// Hover plots (desktop only)
const plots = [
  { name: "Villa 1", x: 2419, y: 1069, dataKey: "villa-1" },
  { name: "Villa 2", x: 2256, y: 1040, dataKey: "villa-2" },
  { name: "Villa 3", x: 2257, y: 1147, dataKey: "villa-3" },
  { name: "Villa 4", x: 2257, y: 1244, dataKey: "villa-4" },
  { name: "Villa 5", x: 2258, y: 1338, dataKey: "villa-5" },
  { name: "Villa 6", x: 2420, y: 1243, dataKey: "villa-6" },
  { name: "Villa 7", x: 2453, y: 1528, dataKey: "villa-7" },
  { name: "Villa 8", x: 2356, y: 1529, dataKey: "villa-8" },
  { name: "Villa 9", x: 2258, y: 1529, dataKey: "villa-9" },
  { name: "Villa 10", x: 2259, y: 1656, dataKey: "villa-10" },
  { name: "Villa 11", x: 2356, y: 1655, dataKey: "villa-11" },
  { name: "Villa 12", x: 2452, y: 1657, dataKey: "villa-12" },
  { name: "Villa 13", x: 2550, y: 1655, dataKey: "villa-13" },
  { name: "Villa 14", x: 2573, y: 1876, dataKey: "villa-14" },
  { name: "Villa 15", x: 2452, y: 1877, dataKey: "villa-15" },
  { name: "Villa 16", x: 2035, y: 2165, dataKey: "villa-16" },
  { name: "Villa 17", x: 2034, y: 2069, dataKey: "villa-17" },
  { name: "Villa 18", x: 2033, y: 1972, dataKey: "villa-18" },
  { name: "Villa 19", x: 2033, y: 1877, dataKey: "villa-19" },
  { name: "Villa 20", x: 2033, y: 1688, dataKey: "villa-20" },
  { name: "Villa 21", x: 2033, y: 1592, dataKey: "villa-21" },
  { name: "Villa 22", x: 2033, y: 1497, dataKey: "villa-22" },
  { name: "Villa 23", x: 2033, y: 1401, dataKey: "villa-23" },
  { name: "Villa 24", x: 2031, y: 1307, dataKey: "villa-24" },
  { name: "Villa 25", x: 2031, y: 1210, dataKey: "villa-25" },
  { name: "Villa 26", x: 2031, y: 1116, dataKey: "villa-26" },
  { name: "Villa 27", x: 2031, y: 998, dataKey: "villa-27" },
  { name: "Villa 28", x: 1901, y: 978, dataKey: "villa-28" },
  { name: "Villa 29", x: 1902, y: 1115, dataKey: "villa-29" },
  { name: "Villa 30", x: 1902, y: 1211, dataKey: "villa-30" },
  { name: "Villa 31", x: 1902, y: 1307, dataKey: "villa-31" },
  { name: "Villa 32", x: 1903, y: 1401, dataKey: "villa-32" },
  { name: "Villa 33", x: 1903, y: 1497, dataKey: "villa-33" },
  { name: "Villa 34", x: 1904, y: 1593, dataKey: "villa-34" },
  { name: "Villa 35", x: 1904, y: 1688, dataKey: "villa-35" },
  { name: "Villa 36", x: 1904, y: 1879, dataKey: "villa-36" },
  { name: "Villa 37", x: 1904, y: 1974, dataKey: "villa-37" },
  { name: "Villa 38", x: 1904, y: 2069, dataKey: "villa-38" },
  { name: "Villa 39", x: 1676, y: 2070, dataKey: "villa-39" },
  { name: "Villa 40", x: 1678, y: 1975, dataKey: "villa-40" },
  { name: "Villa 41", x: 1677, y: 1879, dataKey: "villa-41" },
  { name: "Villa 42", x: 1678, y: 1689, dataKey: "villa-42" },
  { name: "Villa 43", x: 1677, y: 1594, dataKey: "villa-43" },
  { name: "Villa 44", x: 1676, y: 1499, dataKey: "villa-44" },
  { name: "Villa 45", x: 1674, y: 1403, dataKey: "villa-45" },
  { name: "Villa 46", x: 1676, y: 1308, dataKey: "villa-46" },
  { name: "Villa 47", x: 1678, y: 1212, dataKey: "villa-47" },
  { name: "Villa 48", x: 1676, y: 1115, dataKey: "villa-48" },
  { name: "Villa 49", x: 1677, y: 1022, dataKey: "villa-49" },
  { name: "Villa 50", x: 1679, y: 924, dataKey: "villa-50" },
];

const LocationLabels = ({
  labelStyle,
  getResponsivePosition,
  coords,
  onC5Click,
  onC7Click,
  onGateClick,
  onInfoClick,
}) => {
  const [hoveredPlot, setHoveredPlot] = useState(null);
  const [villaMeta, setVillaMeta] = useState({});
  const [loading, setLoading] = useState(true);

  const isTouchDevice = useMemo(
    () => "ontouchstart" in window || navigator.maxTouchPoints > 0,
    []
  );

  // Fetch configuration keyed by villaId
  useEffect(() => {
    const fetchVillaMeta = async () => {
      try {
        const snap = await getDocs(collection(db, "ClarksExoticaVilla"));
        const map = snap.docs.reduce((acc, d) => {
          const data = d.data();
          const key = data.villaId || d.id;

          acc[key] = {
            configuration: data.configuration || "N/A",
          };
          return acc;
        }, {});

        setVillaMeta(map);
      } catch (e) {
        console.error("Error fetching villa meta:", e);
        setVillaMeta({});
      } finally {
        setLoading(false);
      }
    };

    fetchVillaMeta();
  }, []);

  // Hover handling (desktop only) via <map name="projectMap">
  useEffect(() => {
    if (isTouchDevice) return;

    const mapEl = document.querySelector('map[name="projectMap"]');
    if (!mapEl) return;

    const handleOver = (e) => {
      const area = e.target;
      if (!area || area.tagName !== "AREA") return;

      const dataKey = area.getAttribute("data-key");
      if (!dataKey) return;

      const plot = plots.find((p) => p.dataKey === dataKey);
      if (plot) setHoveredPlot(plot);
    };

    const handleOut = (e) => {
      const area = e.target;
      if (!area || area.tagName !== "AREA") return;
      setHoveredPlot(null);
    };

    mapEl.addEventListener("mouseover", handleOver);
    mapEl.addEventListener("mouseout", handleOut);

    return () => {
      mapEl.removeEventListener("mouseover", handleOver);
      mapEl.removeEventListener("mouseout", handleOut);
    };
  }, [isTouchDevice]);

  if (loading) return null;

  return (
    <>
      {/* Persistent tooltips */}
      {tooltips.map((tooltip, index) => {
        const coord = coords[tooltip.coords];
        if (!coord) return null;

        const isClickable =
          tooltip.coords === "c5" ||
          tooltip.coords === "c7" ||
          tooltip.coords === "c18" ||
          tooltip.coords === "cInfo";

        return (
          <div
            key={`tooltip-${index}`}
            className={`persistent-tooltip ${tooltip.className}`}
            onClick={(e) => {
              e.stopPropagation();

              if (tooltip.coords === "c5" && onC5Click) onC5Click();
              if (tooltip.coords === "c7" && onC7Click) onC7Click();
              if (tooltip.coords === "c18" && onGateClick) onGateClick();
              if (tooltip.coords === "cInfo" && onInfoClick) onInfoClick();
            }}
            style={{
              ...labelStyle,
              ...getResponsivePosition(coord.x, coord.y),
              cursor: isClickable ? "pointer" : "default",
              userSelect: "none",
            }}
          >
            {tooltip.name}
          </div>
        );
      })}

      {/* Hover label (desktop only): show Villa Name + Configuration */}
      {!isTouchDevice && hoveredPlot && (
        <div
          className="plot-hover-label"
          style={{
            ...labelStyle,
            ...getResponsivePosition(hoveredPlot.x - 40, hoveredPlot.y - 20),
            transform: "translate(-50%, -50%)",
            lineHeight: 1.15,
            textAlign: "center",
            userSelect: "none",
          }}
        >
          {hoveredPlot.name}
          <br />
          <span style={{ fontWeight: 700 }}>
            {villaMeta[hoveredPlot.dataKey]?.configuration || "N/A"}
          </span>
        </div>
      )}
    </>
  );
};

export default LocationLabels;